import { useState, useCallback, memo, useMemo } from 'react';
import { NavLink } from 'react-router-dom';
import {
  Route as RouteIcon,
  Bell,
  BarChart3,
  Settings,
  X,
  ChevronRight,
  Navigation,
  Users,
  CarFront,
  Plus,
  MapPinned,
  Ticket,
  IndianRupee,
  Calculator,
  CreditCard,
  Clock,
  Activity,
  PieChart,
  BarChart,
  UserCheck,
  Shield,
  FileText,
  HelpCircle,
  AlertTriangle,
  Search
} from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';

const navItems = [
  {
    to: '/dashboard',
    icon: BarChart3,
    label: 'Dashboard',
    // Dashboard is accessible to all authenticated users
    requiredPermissions: ['dashboard_view']
  },
  {
    to: '/trips',
    icon: Navigation,
    label: 'Trips',
    requiredPermissions: ['trips_view', 'trips_manage'],
    subItems: [
      { to: '/trips/active', icon: Activity, label: 'Active Trips', requiredPermissions: ['trips_view'] },
      { to: '/trips/history', icon: Clock, label: 'Trip History', requiredPermissions: ['trips_view'] },
      { to: '/trips/create', icon: Plus, label: 'Create Trip', requiredPermissions: ['trips_manage'] }
      // Removed: Trip Analytics (moved to Global Reports for superadmin)
      // Removed: Ratings & Reviews (moved to separate Ratings module)
    ]
  },
  {
    to: '/users',
    icon: Users,
    label: 'Users',
    requiredPermissions: ['users_view', 'users_manage'],
    subItems: [
      { to: '/users', icon: Users, label: 'All Users', requiredPermissions: ['users_view'] },
      { to: '/users/reports', icon: BarChart, label: 'User Reports', requiredPermissions: ['users_view'] }
    ]
  },
  {
    to: '/drivers',
    icon: UserCheck,
    label: 'Drivers',
    requiredPermissions: ['drivers_view', 'drivers_manage'],
    subItems: [
      { to: '/drivers/applications', icon: UserCheck, label: 'Driver Applications', requiredPermissions: ['drivers_view'] },
      { to: '/drivers/verification', icon: Shield, label: 'Driver Verification', requiredPermissions: ['drivers_manage'] },
      { to: '/drivers/performance', icon: BarChart3, label: 'Performance Analytics', requiredPermissions: ['drivers_view'] },
      { to: '/drivers/documents', icon: FileText, label: 'Document Management', requiredPermissions: ['drivers_manage'] }
    ]
  },
  {
    to: '/cars',
    icon: CarFront,
    label: 'Vehicle ',
    requiredPermissions: ['cars_view', 'cars_manage'],
    subItems: [
      { to: '/cars/all', icon: CarFront, label: 'All Vehicles', requiredPermissions: ['cars_view'] },
      { to: '/cars/documents', icon: FileText, label: 'Vehicle Documents', requiredPermissions: ['cars_manage'] }
    ]
  },
 
  {
    to: '/finance',
    icon: IndianRupee,
    label: 'Finance',
    requiredPermissions: ['cars_manage', 'trips_manage'], // Finance requires admin permissions
    subItems: [
      { to: '/finance/fares', icon: Calculator, label: 'Fare Management', requiredPermissions: ['cars_manage'] },
      { to: '/finance/coupons', icon: Ticket, label: 'Coupons & Discounts', requiredPermissions: ['cars_manage'] },
      { to: '/finance/payments', icon: CreditCard, label: 'Payment Methods', requiredPermissions: ['cars_manage'] },
      { to: '/finance/reports', icon: PieChart, label: 'Financial Reports', requiredPermissions: ['trips_manage'] }
    ]
  },
  {
    to: '/support',
    icon: HelpCircle,
    label: 'Support',
    requiredPermissions: ['support_view'],
    subItems: [
      { to: '/support/disputes', icon: AlertTriangle, label: 'Disputes & Complaints', requiredPermissions: ['support_view'] },
      { to: '/support/lost-found', icon: Search, label: 'Lost & Found', requiredPermissions: ['support_view'] }
    ]},
  {
    to: '/notifications',
    icon: Bell,
    label: 'Notifications',
    requiredPermissions: ['notifications_view']
  },
  // {
  //   to: '/test-protection',
  //   icon: Shield,
  //   label: 'Test Protection',
  //   requiredPermissions: ['dashboard_view'] // Available to all authenticated users
  // }
];

// Memoized Navigation Item Component with Submenu Support
const NavigationItem = memo(({ to, icon: Icon, label, subItems, isCollapsed, onToggleCollapse, isRestricted = false }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const hasSubItems = subItems && subItems.length > 0;

  const toggleExpanded = useCallback((e) => {
    if (hasSubItems && !isCollapsed) {
      e.preventDefault();
      setIsExpanded(!isExpanded);
    }
  }, [hasSubItems, isCollapsed, isExpanded]);

  // For items with submenus, don't navigate directly when expanded
  const handleClick = useCallback((e) => {
    if (hasSubItems) {
      e.preventDefault();
      if (isCollapsed) {
        // If sidebar is collapsed and user clicks submenu parent, expand the sidebar
        if (onToggleCollapse) {
          onToggleCollapse();
        }
      } else {
        // If sidebar is expanded, toggle the submenu
        toggleExpanded(e);
      }
    }
  }, [hasSubItems, isCollapsed, toggleExpanded, onToggleCollapse]);

  return (
    <div className="nav-item-container">
      <div className="relative group">
        {hasSubItems ? (
          // Menu item with submenu
          <button
            onClick={handleClick}
            className={`w-full flex items-center rounded-xl transition-all duration-200 focus-ring nav-submenu-parent`}
            style={{
              padding: isCollapsed ? 'var(--space-3)' : 'var(--space-4)',
              gap: isCollapsed ? '0' : 'var(--space-3)',
              justifyContent: isCollapsed ? 'center' : 'space-between'
            }}
          >
            <div className="flex items-center" style={{ gap: isCollapsed ? '0' : 'var(--space-3)' }}>
              <Icon size={20} style={{ filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.1))' }} />
              <span className={`nav-text text-sm font-medium ${isCollapsed ? 'nav-text-hidden' : 'nav-text-visible'}`}>
                {label}
              </span>
              {!isCollapsed && isRestricted && (
                <div
                  className="w-2 h-2 bg-orange-400 rounded-full opacity-60"
                  title="Some items may be hidden based on your permissions"
                />
              )}
            </div>
            {!isCollapsed && (
              <ChevronRight
                size={16}
                className={`transition-transform duration-200 ${isExpanded ? 'rotate-90' : ''}`}
                style={{ color: 'var(--text-sidebar)' }}
              />
            )}
          </button>
        ) : (
          // Regular menu item without submenu
          <NavLink
            to={to}
            className={({ isActive }) =>
              `flex items-center rounded-xl transition-all duration-200 focus-ring ${
                isActive ? 'nav-active' : 'nav-inactive'
              }`
            }
            style={{
              padding: isCollapsed ? 'var(--space-3)' : 'var(--space-4)',
              gap: isCollapsed ? '0' : 'var(--space-3)',
              justifyContent: isCollapsed ? 'center' : 'flex-start'
            }}
          >
            <Icon size={20} style={{ filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.1))' }} />
            <span className={`nav-text text-sm font-medium ${isCollapsed ? 'nav-text-hidden' : 'nav-text-visible'}`}>
              {label}
            </span>
          </NavLink>
        )}

        {/* Tooltip - Always rendered but conditionally visible */}
        <div
          className={`nav-tooltip absolute left-full ml-2 px-3 py-2 text-sm font-medium text-white rounded-lg transition-opacity duration-200 pointer-events-none z-50 whitespace-nowrap ${
            isCollapsed ? 'nav-tooltip-visible' : 'nav-tooltip-hidden'
          }`}
          style={{
            backgroundColor: 'rgba(15, 23, 42, 0.9)',
            backdropFilter: 'blur(8px)',
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            top: '50%',
            transform: 'translateY(-50%)',
            border: '1px solid rgba(255,255,255,0.1)'
          }}
        >
          {label}
          <div
            className="absolute right-full top-1/2 transform -translate-y-1/2"
            style={{
              width: 0,
              height: 0,
              borderTop: '6px solid transparent',
              borderBottom: '6px solid transparent',
              borderRight: '6px solid rgba(15, 23, 42, 0.9)'
            }}
          />
        </div>
      </div>

      {/* Submenu - Only show when expanded and not collapsed */}
      {hasSubItems && isExpanded && !isCollapsed && (
        <div
          className="submenu-container ml-6 mt-2 space-y-1 animate-fade-in"
          style={{ borderLeft: '2px solid rgba(148, 163, 184, 0.2)' }}
        >
          {subItems.map(({ to: subTo, icon: SubIcon, label: subLabel }) => (
            <NavLink
              key={subTo}
              to={subTo}
              className={({ isActive }) =>
                `flex items-center rounded-lg transition-all duration-200 focus-ring pl-4 py-2 ${
                  isActive ? 'submenu-active' : 'submenu-inactive'
                }`
              }
              style={{ gap: 'var(--space-2)' }}
            >
              <SubIcon size={16} style={{ filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.1))' }} />
              <span className="text-xs font-medium">{subLabel}</span>
            </NavLink>
          ))}
        </div>
      )}
    </div>
  );
});

NavigationItem.displayName = 'NavigationItem';

export function Sidebar({ isOpen, onClose }) {
  const { user, hasAnyPermission } = useAuth();

  // Use localStorage to persist sidebar state across navigation
  const [isCollapsed, setIsCollapsed] = useState(() => {
    const saved = localStorage.getItem('sidebar-collapsed');
    return saved !== null ? JSON.parse(saved) : true; // Default to collapsed (icons only)
  });

  const toggleCollapse = useCallback(() => {
    const newState = !isCollapsed;
    setIsCollapsed(newState);
    // Save state to localStorage to persist across navigation
    localStorage.setItem('sidebar-collapsed', JSON.stringify(newState));
  }, [isCollapsed]);

  // Filter navigation items based on user permissions
  const filteredNavItems = useMemo(() => {
    if (!user) return [];

    return navItems.filter(item => {
      // If no permissions required, show the item
      if (!item.requiredPermissions || item.requiredPermissions.length === 0) {
        return true;
      }

      // Check if user has any of the required permissions
      const hasPermission = hasAnyPermission(item.requiredPermissions);

      if (hasPermission && item.subItems) {
        // Filter sub-items based on permissions
        item.filteredSubItems = item.subItems.filter(subItem => {
          if (!subItem.requiredPermissions || subItem.requiredPermissions.length === 0) {
            return true;
          }
          return hasAnyPermission(subItem.requiredPermissions);
        });
      }

      return hasPermission;
    });
  }, [user, hasAnyPermission]);

  return (
    <aside
      className={`fixed z-50 top-0 left-0 h-full transform transition-all duration-300 lg:static lg:translate-x-0 ${
        isOpen ? 'translate-x-0' : '-translate-x-full'
      }`}
      style={{
        width: isCollapsed ? '80px' : '280px',
        backgroundColor: 'var(--bg-sidebar)',
        borderRight: '1px solid var(--border-primary)',
        boxShadow: 'var(--shadow-lg)'
      }}
    >
      {/* Mobile close button */}
      <div className="lg:hidden flex justify-end p-4">
        <button
          onClick={onClose}
          className="p-2 rounded-lg transition-all duration-200 hover:scale-105 focus-ring"
          style={{
            backgroundColor: 'var(--bg-hover)',
            color: 'var(--text-secondary)'
          }}
        >
          <X size={20} />
        </button>
      </div>

      <div className="flex flex-col h-full px-4 py-6">
        {/* Header with Logo */}
        <div
          className={`mb-8 flex items-center transition-all duration-300 flex-shrink-0 ${
            isCollapsed ? 'justify-center' : ''
          }`}
          style={{
            gap: isCollapsed ? '0' : 'var(--space-3)'
          }}
        >
          <div
            className="flex items-center justify-center font-bold transition-all duration-300 gradient-primary relative group"
            style={{
              width: isCollapsed ? '44px' : '48px',
              height: isCollapsed ? '44px' : '48px',
              borderRadius: 'var(--radius-xl)',
              boxShadow: '0 8px 32px rgba(102, 126, 234, 0.3)',
              fontSize: isCollapsed ? '20px' : '22px',
              color: '#ffffff',
              border: '2px solid rgba(255, 255, 255, 0.2)'
            }}
          >
            🚕

            {/* Tooltip for collapsed logo */}
            {isCollapsed && (
              <div
                className="logo-tooltip absolute left-full ml-2 px-3 py-2 text-sm font-medium text-white rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-50 whitespace-nowrap"
                style={{
                  backgroundColor: 'rgba(15, 23, 42, 0.9)',
                  backdropFilter: 'blur(8px)',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  border: '1px solid rgba(255,255,255,0.1)'
                }}
              >
                CabTrack Pro
                {/* Arrow */}
                <div
                  className="absolute right-full top-1/2 transform -translate-y-1/2"
                  style={{
                    width: 0,
                    height: 0,
                    borderTop: '6px solid transparent',
                    borderBottom: '6px solid transparent',
                    borderRight: '6px solid rgba(15, 23, 42, 0.9)'
                  }}
                />
              </div>
            )}
          </div>

          {/* Logo Text - Always rendered but conditionally visible */}
          <span
            className={`logo-text text-xl font-bold transition-all duration-300 ${
              isCollapsed ? 'logo-hidden' : 'logo-visible'
            }`}
            style={{
              fontWeight: 'var(--font-bold)',
              color: 'var(--text-sidebar)',
              background: 'linear-gradient(135deg, #e2e8f0, #cbd5e1)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text'
            }}
          >
            CabTrack Pro
          </span>
        </div>

        {/* Navigation - Scrollable area */}
        <nav
          className="flex-1 flex flex-col overflow-hidden"
          style={{ gap: 'var(--space-2)' }}
        >
          <div
            className="flex flex-col overflow-y-auto scrollbar-hidden"
            style={{ gap: 'var(--space-2)', paddingRight: '4px' }}
          >
            {filteredNavItems.map(({ to, icon, label, subItems, filteredSubItems }) => {
              const hasFilteredSubItems = filteredSubItems && filteredSubItems.length !== subItems?.length;
              return (
                <NavigationItem
                  key={to}
                  to={to}
                  icon={icon}
                  label={label}
                  subItems={filteredSubItems || subItems}
                  isCollapsed={isCollapsed}
                  onToggleCollapse={toggleCollapse}
                  isRestricted={hasFilteredSubItems}
                />
              );
            })}
          </div>
        </nav>

        {/* Fixed Bottom Section */}
        <div className="flex-shrink-0">
          {/* Settings */}
          <div
            className="pt-4 settings-container"
            style={{ borderTop: '1px solid var(--border-primary)' }}
          >
            <NavigationItem
              to="/settings"
              icon={Settings}
              label="Settings"
              isCollapsed={isCollapsed}
              onToggleCollapse={toggleCollapse}
            />
          </div>

          {/* Collapse Toggle Button */}
          <div
            className="pt-4 hidden lg:block collapse-container"
            style={{ borderTop: '1px solid var(--border-primary)' }}
          >
          <div className="relative group">
            <button
              onClick={toggleCollapse}
              className="w-full flex items-center rounded-xl transition-all duration-200 focus-ring collapse-button"
              style={{
                padding: isCollapsed ? 'var(--space-3)' : 'var(--space-4)',
                gap: isCollapsed ? '0' : 'var(--space-3)',
                color: '#94a3b8',
                backgroundColor: 'transparent',
                justifyContent: isCollapsed ? 'center' : 'flex-start'
              }}
            >
              {/* Icon - Always rendered but conditionally shown */}
              <ChevronRight
                size={20}
                style={{ filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.1))' }}
                className={`collapse-icon transition-transform duration-300 ${
                  isCollapsed ? 'collapse-icon-collapsed' : 'collapse-icon-expanded'
                }`}
              />

              {/* Text - Always rendered but conditionally visible */}
              <span className={`collapse-text text-sm font-medium ${
                isCollapsed ? 'collapse-text-hidden' : 'collapse-text-visible'
              }`}>
                Collapse
              </span>
            </button>

            {/* Tooltip - Always rendered but conditionally visible */}
            <div
              className={`collapse-tooltip absolute left-full ml-2 px-3 py-2 text-sm font-medium text-white rounded-lg transition-opacity duration-200 pointer-events-none z-50 whitespace-nowrap ${
                isCollapsed ? 'collapse-tooltip-visible' : 'collapse-tooltip-hidden'
              }`}
              style={{
                backgroundColor: 'rgba(15, 23, 42, 0.9)',
                backdropFilter: 'blur(8px)',
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                top: '50%',
                transform: 'translateY(-50%)',
                border: '1px solid rgba(255,255,255,0.1)'
              }}
            >
              Expand Sidebar
              <div
                className="absolute right-full top-1/2 transform -translate-y-1/2"
                style={{
                  width: 0,
                  height: 0,
                  borderTop: '6px solid transparent',
                  borderBottom: '6px solid transparent',
                  borderRight: '6px solid rgba(15, 23, 42, 0.9)'
                }}
              />
            </div>
          </div>
        </div>
        </div>
      </div>
    </aside>
  );
}
